import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const STORAGE_KEY = 'savedProjects';
const LAST_KEY = 'lastProjectSlug';
const ATTEMPTS_KEY = 'projectAddAttempts';
const MAX_ATTEMPTS = 6;
const LOCK_MINUTES = 30;

const loadProjects = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const saveProjects = (projects) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
};

const loadAttempts = () => {
  try {
    const raw = localStorage.getItem(ATTEMPTS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveAttempts = (attempts) => {
  localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(attempts));
};

const resetAttempts = () => {
  localStorage.removeItem(ATTEMPTS_KEY);
};

function ProjectSelector({
  value,
  onChange,
  onAdded,
  onNameChange,
  label,
  helperText,
  textFieldProps,
  addIconSx,
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [projects, setProjects] = useState(loadProjects());
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogName, setDialogName] = useState('');

  useEffect(() => {
    const match = projects.find((p) => p.slug === value);
    setInputValue(match?.name || value || '');
  }, [value, projects]);

  const sortedProjects = useMemo(() => {
    const copy = [...projects];
    copy.sort((a, b) => {
      const pinA = a?.pinned ? 1 : 0;
      const pinB = b?.pinned ? 1 : 0;
      if (pinA !== pinB) return pinB - pinA;
      return (a?.slug || '').localeCompare(b?.slug || '');
    });
    return copy;
  }, [projects]);

  const handleSelect = (_event, option) => {
    if (typeof option === 'string') {
      setInputValue(option);
      onNameChange?.(option);
      onChange('');
      localStorage.removeItem(LAST_KEY);
      return;
    }
    const slug = option?.slug || '';
    setInputValue(option?.name || slug);
    onNameChange?.(option?.name || '');
    onChange(slug);
    if (slug) {
      localStorage.setItem(LAST_KEY, slug);
    } else {
      localStorage.removeItem(LAST_KEY);
    }
  };

  const handleTogglePin = (slug) => {
    const next = projects.map((p) =>
      p.slug === slug ? { ...p, pinned: !p.pinned } : p,
    );
    setProjects(next);
    saveProjects(next);
  };

  const handleDelete = (slug) => {
    const next = projects.filter((p) => p.slug !== slug);
    setProjects(next);
    saveProjects(next);
    if (inputValue === slug) {
      setInputValue('');
      onChange('');
      localStorage.removeItem(LAST_KEY);
    }
  };

  const getLockInfo = () => {
    const attempts = loadAttempts();
    if (!attempts?.lockedUntil) return null;
    const now = Date.now();
    if (now >= attempts.lockedUntil) {
      resetAttempts();
      return null;
    }
    const remainingMs = attempts.lockedUntil - now;
    const remainingMin = Math.ceil(remainingMs / 60000);
    return remainingMin;
  };

  const registerFailure = () => {
    const now = Date.now();
    const attempts = loadAttempts() || { count: 0, lockedUntil: null };
    const nextCount = attempts.count + 1;
    const next = { count: nextCount, lockedUntil: attempts.lockedUntil };
    if (nextCount >= MAX_ATTEMPTS) {
      next.lockedUntil = now + LOCK_MINUTES * 60 * 1000;
    }
    saveAttempts(next);
  };

  const handleAdd = async () => {
    setError('');
    const name = String(dialogName || '').trim();
    if (!name) {
      setError('Введите название проекта');
      return;
    }
    const lockLeft = getLockInfo();
    if (lockLeft) {
      setError(`Слишком много ошибок. Попробуйте через ${lockLeft} мин.`);
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(`/api/p/_/check?name=${encodeURIComponent(name)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data?.exists) {
        registerFailure();
        setError('Проект не найден');
        return;
      }
      const resolvedSlug = String(data?.slug || '').trim();
      if (!resolvedSlug) {
        setError('Не удалось определить slug проекта');
        return;
      }
      resetAttempts();
      const existing = projects.find((p) => p.slug === resolvedSlug);
      const next = existing
        ? projects.map((p) => (p.slug === resolvedSlug ? { ...p, name: name || p.name } : p))
        : [...projects, { slug: resolvedSlug, name: name || undefined, pinned: false }];
      setProjects(next);
      saveProjects(next);
      localStorage.setItem(LAST_KEY, resolvedSlug);
      onChange(resolvedSlug);
      if (onAdded) onAdded(resolvedSlug);
      setDialogOpen(false);
    } catch (e) {
      setError('Ошибка проверки проекта');
    } finally {
      setChecking(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        fullWidth
        freeSolo
        options={sortedProjects}
        value={sortedProjects.find((p) => p.slug === value) || null}
        inputValue={inputValue}
        onInputChange={(_e, newValue) => {
          const normalized = String(newValue || '').trim().toLowerCase();
          const match = projects.find((p) => {
            const name = (p.name || '').trim().toLowerCase();
            const slug = (p.slug || '').trim().toLowerCase();
            return normalized && (name === normalized || slug === normalized);
          });
          if (match) {
            setInputValue(match.name || match.slug);
            onChange(match.slug);
            onNameChange?.(match.name || '');
            localStorage.setItem(LAST_KEY, match.slug);
            return;
          }
          setInputValue(newValue);
          onNameChange?.(newValue || '');
          onChange('');
          if (!newValue) {
            localStorage.removeItem(LAST_KEY);
          }
        }}
        onChange={handleSelect}
        getOptionLabel={(option) => (typeof option === 'string' ? option : (option.name || option.slug))}
        renderOption={(props, option) => (
          <li {...props} key={option.slug}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ lineHeight: 1.2 }}>
                  {option.name || option.slug}
                </Typography>
                {option.name && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {option.slug}
                  </Typography>
                )}
              </Box>
              <Tooltip title={option.pinned ? 'Открепить' : 'Закрепить'}>
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePin(option.slug);
                }}>
                  {option.pinned ? <PushPinIcon fontSize="inherit" /> : <PushPinOutlinedIcon fontSize="inherit" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Удалить из списка">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(option.slug);
                }}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </li>
        )}
        renderInput={(params) => {
          const tfProps = textFieldProps || {};
          const extraInputProps = tfProps.InputProps || {};
          const extraInputLabelProps = tfProps.InputLabelProps || {};
          const extraFormHelperTextProps = tfProps.FormHelperTextProps || {};
          const extraInputInnerProps = tfProps.inputProps || {};
          const restTf = { ...tfProps };
          delete restTf.InputProps;
          delete restTf.InputLabelProps;
          delete restTf.FormHelperTextProps;
          delete restTf.inputProps;
          const existingEndAdornment = params.InputProps?.endAdornment;
          return (
            <TextField
              {...params}
              {...restTf}
              label={label}
              helperText={error || helperText}
              error={Boolean(error)}
              inputProps={{
                ...params.inputProps,
                ...extraInputInnerProps,
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...extraInputLabelProps,
              }}
              FormHelperTextProps={{
                ...extraFormHelperTextProps,
              }}
              InputProps={{
                ...params.InputProps,
                ...extraInputProps,
                endAdornment: (
                  <>
                    {existingEndAdornment}
                    {extraInputProps.endAdornment}
                    <InputAdornment position="end">
                      <Tooltip title="Добавить проект">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          const trimmed = String(inputValue || '').trim();
                          setDialogName(trimmed);
                          setDialogOpen(true);
                        }}
                        disabled={checking}
                        sx={addIconSx}
                      >
                          <AddIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  </>
                ),
              }}
            />
          );
        }}
      />
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Добавить проект</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название (как вам удобно)"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            helperText="Введите название проекта"
            sx={{ mt: 1 }}
          />
          {!!error && (
            <Typography sx={{ mt: 2 }} color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleAdd} disabled={checking} variant="contained">
            {checking ? 'Проверка...' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ProjectSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onAdded: PropTypes.func,
  onNameChange: PropTypes.func,
  label: PropTypes.string,
  helperText: PropTypes.string,
  textFieldProps: PropTypes.object,
  addIconSx: PropTypes.object,
};

ProjectSelector.defaultProps = {
  value: '',
  onAdded: null,
  onNameChange: null,
  label: 'Проект (название)',
  helperText: 'Выберите из списка или нажмите + чтобы добавить',
  textFieldProps: undefined,
  addIconSx: undefined,
};

export default ProjectSelector;

